import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { IStoreOperatorRecord } from "@/services/store-access-query";

import { DataTableColumnHeader } from "../shared/data-table-column-header";
import { SituationCell } from "@/components/unlock-request/situation-cell";

import { OperatorRowActions } from "./row-actions";

interface UseOperatorColumnsOptions {
  storeCode: number;
}

export const useOperatorColumns = (
  options: UseOperatorColumnsOptions,
): ColumnDef<IStoreOperatorRecord>[] => {
  const { storeCode } = options;

  return useMemo(
    () => [
      {
        accessorKey: "cpf",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="CPF" />
        ),
        cell: ({ row }) => row.original.cpf || "-",
      },
      {
        accessorKey: "operatorCode",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Código" />
        ),
        cell: ({ row }) => row.original.operatorCode || "-",
      },
      {
        id: "name",
        accessorKey: "operatorName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Nome" />
        ),
        cell: ({ row }) => row.original.operatorName || "-",
      },
      {
        accessorKey: "classification",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Classificação" />
        ),
        cell: ({ row }) => {
          const classification = row.original.classification;
          if (classification === "Caixa") {
            return <Badge variant="secondary">Caixa</Badge>;
          }
          if (classification === "Gerente") {
            return <Badge variant="secondary">Gerente</Badge>;
          }
          return <Badge variant="outline">-</Badge>;
        },
      },
      {
        accessorKey: "situation",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Situação" />
        ),
        cell: ({ row }) => (
          <SituationCell
            situation={row.original.situation}
            situationStatus={row.original.situationStatus}
          />
        ),
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Data Criação" />
        ),
        cell: ({ row }) => {
          const date = row.original.createdAt;
          if (!date) return "-";
          return new Date(date).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "blocked",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => {
          const blocked = row.original.blocked;
          return (
            <Badge variant={blocked ? "destructive" : "outline"}>
              {blocked ? "Bloqueado" : "Ativo"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Ações" />
        ),
        cell: ({ row }) => (
          <OperatorRowActions record={row.original} storeCode={storeCode} />
        ),
      },
    ],
    [storeCode],
  );
};
